import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// Utils
import moment from "moment-timezone";

// Redux
import { useSelector, useDispatch } from "react-redux";

// Routing
import { useHistory, useLocation } from "react-router-dom";

// Components
import Checkbox from "@material-ui/core/Checkbox";
import Grid from "@material-ui/core/Grid";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { AgGridReact } from "ag-grid-react";

// Icons
import VisibilityIcon from "@material-ui/icons/Visibility";
import { Button, Snackbar } from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";

import {
  fetchOrders,
  editOrder,
} from "../../../redux/features/sales/ordersSlice";

const toCents = (amount) => Math.round((parseFloat(amount) || 0) * 100);

// Discount-adjusted total actually owed on the order (falls back to total when no discount applies)
const getEffectiveTotalCents = (data) =>
  toCents(data?.post_discount_total ?? data?.total);

const getNetPaidCents = (data) =>
  toCents(data?.total_paid) - toCents(data?.total_refunded);

const escapeOverlayMessage = (message) => {
  if (!message) {
    return "";
  }

  return message
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
};

const OrdersPage = () => {
  // Routing
  const history = useHistory();
  const location = useLocation();

  // User Permission from redux
  const { userPermission } = useSelector((state) => {
    return state.auth;
  });
  const { write } = userPermission?.sales ?? { write: false };

  // Redux
  const { mode } = useSelector((state) => state.ui);

  // Local State
  const [showSuccess, setShowSuccess] = useState(false);
  const [snackbarContent, setSnackbarContent] = useState("");
  const [snackbarLink, setSnackbarLink] = useState("");
  const [orderType, setOrderType] = useState("all");

  useEffect(() => {
    if (location.state) {
      setShowSuccess(location.state.showSuccess);
      setSnackbarContent(location.state.snackbarContent);
      setSnackbarLink(location.state.snackbarLink);
    }
    return () => {
      setShowSuccess(false);
      setSnackbarContent("");
      setSnackbarLink("");
    };
  }, [location.state]);

  const handleSnackbarClose = () => {
    setShowSuccess(false);
    setSnackbarContent("");
  };

  // Local State to show only upcoming check ins
  const [showUpcoming, setShowUpcoming] = useState(false);

  // Redux
  const dispatch = useDispatch();
  const { ordersList, isLoading, error } = useSelector((state) => state.orders);
  const errorMessage = error?.errorMessage || "";

  const gridApiRef = useRef(null);
  const suppressCellChangeRef = useRef(false);

  const displayedOrders = useMemo(() => {
    return showUpcoming
      ? ordersList.filter((order) => {
          return moment(order.check_in).isAfter(moment());
        })
      : ordersList;
  }, [ordersList, showUpcoming]);

  const rowData = useMemo(() => {
    return displayedOrders.map((order) => ({
      ...order,
    }));
  }, [displayedOrders]);

  const rowCount = rowData.length;

  const currencyFormatter = useMemo(() => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    });
  }, []);

  const ActionCellRenderer = useCallback(
    (params) => {
      const { value } = params;

      return (
        <a
          href={`/sales/orders/${value}`}
          onClick={(e) => {
            if (!e.ctrlKey && !e.metaKey && !e.shiftKey && e.button === 0) {
              e.preventDefault();
              history.push(`/sales/orders/${value}`);
            }
          }}
          style={{ textDecoration: "none" }}
        >
          <Button
            size="small"
            component="span"
            startIcon={<VisibilityIcon />}
            color="primary"
          >
            View
          </Button>
        </a>
      );
    },
    [history]
  );

  const columnDefs = useMemo(() => {
    return [
      {
        headerName: "Order ID",
        field: "id",
        filter: "agNumberColumnFilter",
        pinned: "left",
        width: 120,
      },
      {
        headerName: "Customer ID",
        field: "customer_id",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Customer Name",
        field: "customer_name",
        width: 160,
        pinned: "left",
        cellStyle: {
          whiteSpace: "nowrap",
        },
      },
      {
        headerName: "Type",
        field: "type",
        filter: "agTextColumnFilter",
        valueFormatter: (params) => {
          if (params.value === "condo") {
            return "CONDO";
          }
          if (params.value === "package") {
            return "PACKAGE";
          }
          return params.value;
        },
      },
      {
        headerName: "Date Placed",
        field: "date_order_placed",
        minWidth: 180,
        valueFormatter: (params) => {
          if (!params.value) {
            return "";
          }

          return moment(params.value).tz("America/Chicago").format("lll");
        },
        cellStyle: {
          whiteSpace: "nowrap",
        },
      },
      {
        headerName: "Check In",
        field: "check_in",
        minWidth: 140,
        valueFormatter: (params) => {
          return params.value ? moment(params.value).utc().format("ll") : "";
        },
        cellStyle: {
          whiteSpace: "nowrap",
        },
      },
      {
        headerName: "Check Out",
        field: "check_out",
        minWidth: 140,
        valueFormatter: (params) => {
          return params.value ? moment(params.value).utc().format("ll") : "";
        },
        cellStyle: {
          whiteSpace: "nowrap",
        },
      },
      {
        headerName: "Package",
        field: "package_name",
      },
      {
        headerName: "Hotel",
        field: "hotel_name",
        cellStyle: (params) => {
          const { value } = params;

          if (!value) {
            return undefined;
          }

          // Generate consistent hex color based on text value
          const getColorFromText = (text) => {
            switch (text) {
              case "La Copa Inn Beach Hotel":
                return "#1c9ded";
              case "Sand Rose Beach Resort":
                return "#a55bcd";
              case "Courtyard by Marriott":
                return "#c49332";
              case "Hilton Garden Inn":
                return "#21ac80";
              default:
                return "#536263";
            }
          };

          return {
            color: "white",
            backgroundColor: getColorFromText(value),
            fontWeight: "bold",
          };
        },
      },
      {
        headerName: "Room",
        field: "room_name",
      },
      {
        headerName: "Rates Subtotal",
        field: "rates_subtotal",
        type: "rightAligned",
        valueFormatter: (params) => {
          return params.value
            ? currencyFormatter.format(Number(params.value))
            : "";
        },
      },
      {
        headerName: "Rates Tax",
        field: "rates_tax",
        type: "rightAligned",
        valueFormatter: (params) => {
          return params.value
            ? currencyFormatter.format(Number(params.value))
            : "";
        },
      },
      {
        headerName: "Addons Subtotal",
        field: "addons_subtotal",
        type: "rightAligned",
        valueFormatter: (params) => {
          return params.value
            ? currencyFormatter.format(Number(params.value))
            : "";
        },
      },
      {
        headerName: "Addons Tax",
        field: "addons_tax",
        type: "rightAligned",
        valueFormatter: (params) => {
          return params.value
            ? currencyFormatter.format(Number(params.value))
            : "";
        },
      },
      {
        headerName: "Subtotal",
        field: "subtotal",
        type: "rightAligned",
        valueFormatter: (params) => {
          return params.value
            ? currencyFormatter.format(Number(params.value))
            : "";
        },
      },
      {
        headerName: "Tax",
        field: "tax",
        type: "rightAligned",
        valueFormatter: (params) => {
          return params.value
            ? currencyFormatter.format(Number(params.value))
            : "";
        },
      },
      {
        headerName: "Resort Fee",
        field: "resort_fee_total",
        type: "rightAligned",
        valueFormatter: (params) => {
          return params.value
            ? currencyFormatter.format(Number(params.value))
            : "";
        },
      },
      {
        headerName: "Discount",
        field: "discount_amount",
        type: "rightAligned",
        valueFormatter: (params) => {
          return params.value
            ? currencyFormatter.format(Number(params.value))
            : "";
        },
      },
      {
        headerName: "Total",
        field: "total",
        type: "rightAligned",
        valueFormatter: (params) => {
          return params.value
            ? currencyFormatter.format(Number(params.value))
            : "";
        },
      },
      {
        headerName: "Total Paid",
        field: "total_paid",
        type: "rightAligned",
        valueFormatter: (params) => {
          return params.value
            ? currencyFormatter.format(Number(params.value))
            : "";
        },
      },
      {
        headerName: "Total Refunded",
        field: "total_refunded",
        type: "rightAligned",
        valueFormatter: (params) => {
          return params.value
            ? currencyFormatter.format(Number(params.value))
            : "";
        },
        cellStyle: (params) => {
          if (params.value && params.value !== "0.00") {
            return {
              color: "white",
              backgroundColor: "#de4141bb",
              fontWeight: "bold",
            };
          }
          return undefined;
        },
      },
      {
        headerName: "Net Amount Paid",
        field: "net_amount_paid",
        type: "rightAligned",
        valueFormatter: (params) => {
          return params.value
            ? currencyFormatter.format(Number(params.value))
            : "";
        },
        cellStyle: (params) => {
          const { value, data } = params;

          if (!value) {
            return undefined;
          }

          if (value === "0.00") {
            return {
              color: "white",
              backgroundColor: "#de4141bb",
              fontWeight: "bold",
            };
          }

          if (getEffectiveTotalCents(data) === getNetPaidCents(data)) {
            return {
              color: "white",
              backgroundColor: "#19a86dbb",
              fontWeight: "bold",
            };
          }

          return {
            color: "white",
            backgroundColor: "#dfad1dbb",
            fontWeight: "bold",
          };
        },
      },
      {
        headerName: "Total Owed",
        field: "total_owed",
        type: "rightAligned",
        valueFormatter: (params) => {
          return params.value
            ? currencyFormatter.format(Number(params.value))
            : "";
        },
        cellStyle: (params) => {
          if (params.value && params.value !== "0.00") {
            return {
              color: "white",
              backgroundColor: "#dfad1dbb",
              fontWeight: "bold",
            };
          }
          return undefined;
        },
      },
      {
        headerName: "Charge Remaining By",
        field: "charge_remaining_by",
        minWidth: 150,
        valueFormatter: (params) => {
          const { data } = params;

          if (!data) {
            return "";
          }

          if (
            data.order_status === "COMPLETE" ||
            data.order_status === "CANCELLED" ||
            getNetPaidCents(data) >= getEffectiveTotalCents(data)
          ) {
            return "";
          }

          return params.value ? moment(params.value).format("ll") : "";
        },
        cellStyle: (params) => {
          const { data } = params;

          if (!data) {
            return undefined;
          }

          if (
            data.order_status === "PENDING" &&
            getNetPaidCents(data) < getEffectiveTotalCents(data)
          ) {
            return {
              whiteSpace: "nowrap",
              color: "white",
              backgroundColor: "#dfad1dbb",
              fontWeight: "bold",
            };
          }

          return {
            whiteSpace: "nowrap",
          };
        },
      },
      {
        headerName: "Status",
        field: "order_status",
        filter: "agTextColumnFilter",
        editable: true,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: {
          values: ["PENDING", "COMPLETE", "CANCELLED"],
        },
        cellStyle: (params) => {
          if (params.value === "PENDING") {
            return {
              color: "white",
              backgroundColor: "#dfad1dbb",
              fontWeight: "bold",
            };
          }

          if (params.value === "COMPLETE") {
            return {
              color: "white",
              backgroundColor: "#19a86dbb",
              fontWeight: "bold",
            };
          }

          if (params.value === "CANCELLED") {
            return {
              color: "white",
              backgroundColor: "#de4141bb",
              fontWeight: "bold",
            };
          }

          return undefined;
        },
      },
      {
        headerName: "Reservation Status",
        field: "reservation_status",
        filter: "agTextColumnFilter",
        editable: true,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: {
          values: ["PENDING", "RESERVED", "CANCELLED"],
        },
        cellStyle: (params) => {
          if (params.value === "PENDING") {
            return {
              color: "white",
              backgroundColor: "#dfad1dbb",
              fontWeight: "bold",
            };
          }

          if (params.value === "RESERVED") {
            return {
              color: "white",
              backgroundColor: "#19a86dbb",
              fontWeight: "bold",
            };
          }

          if (params.value === "CANCELLED") {
            return {
              color: "white",
              backgroundColor: "#de4141bb",
              fontWeight: "bold",
            };
          }

          return undefined;
        },
      },
      {
        headerName: "Actions",
        field: "id",
        pinned: "right",
        width: 140,
        cellRendererFramework: ActionCellRenderer,
        sortable: false,
        filter: false,
        resizable: false,
      },
    ];
  }, [ActionCellRenderer, currencyFormatter]);

  // Filter orders by upcoming check ins
  const defaultColDef = useMemo(() => {
    return {
      sortable: true,
      filter: true,
      resizable: true,
      flex: 1,
      minWidth: 120,
    };
  }, []);

  const onBtnExport = () => {
    const api = gridApiRef.current;
    if (!api) {
      return;
    }

    // TODO: allow user to select columns to export
    const csvExportParams = {
      fileName: `pbx_package_orders_${moment().format("YYYYMMDD_HHmmss")}.csv`,
      shouldRowBeSkipped: ({ node }) => {
        // skip rows with type = "condo"
        return node.data && node.data.type === "condo";
      },
      processCellCallback: (params) => {
        const { column, value } = params;
        if (column.colId === "date_order_placed") {
          // 2025-09-27T17:48:25.000Z -> 2025-09-27 17:48:25.000
          return moment(value).format("YYYY-MM-DD HH:mm:ss.SSS");
        }
        if (column.colId === "check_in" || column.colId === "check_out") {
          return value ? moment(value).utc().format("YYYY-MM-DD") : "";
        }
        return value;
      },
      allColumns: false,
      columnKeys: [
        "id",
        // "customer_id",
        "customer_name",
        // "type",
        "date_order_placed",
        "check_in",
        "check_out",
        "package_name",
        "hotel_name",
        "room_name",
        "rates_subtotal",
        "rates_tax",
        "addons_subtotal",
        "addons_tax",
        "subtotal",
        "tax",
        "discount_amount",
        "total",
        "total_paid",
        "total_refunded",
        // "net_amount_paid",
        // "charge_remaining_by",
        "order_status",
        // "reservation_status",
      ],
    };

    api.exportDataAsCsv(csvExportParams);
  };

  const handleOrderType = (event, newOrderType) => {
    // Prevent null (deselection) – default back to "all"
    const next = newOrderType ?? "all";
    setOrderType(next);

    const api = gridApiRef.current;

    if (!api) return;

    if (next === "all") {
      // Clear only the 'type' filter (or all if no other filters are used)
      const model = api.getFilterModel() || {};
      delete model.type;
      api.setFilterModel(model);
      return;
    }
    // Set the 'type' filter to either "package" or "condo"
    api.setFilterModel({
      type: { filterType: "text", type: "equals", filter: next },
    });
  };

  const onCellValueChanged = useCallback(
    async (params) => {
      if (suppressCellChangeRef.current) return;
      const { data, colDef, newValue, oldValue } = params;
      if (newValue === oldValue) return;

      let orderObj = {};
      if (colDef.field === "order_status") {
        orderObj = { status: newValue };
      } else if (colDef.field === "reservation_status") {
        orderObj = { reservation_status: newValue };
      } else {
        return;
      }

      const res = await dispatch(editOrder(data.id, orderObj));
      if (!res.error) {
        dispatch(fetchOrders());
        setSnackbarContent("Order updated successfully!");
        setShowSuccess(true);
      } else {
        suppressCellChangeRef.current = true;
        params.node.setDataValue(colDef.field, oldValue);
        suppressCellChangeRef.current = false;
        setSnackbarContent(
          "There was an error updating the order. Please try again."
        );
        setShowSuccess(true);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    dispatch(fetchOrders());
  }, [dispatch]);

  const onGridReady = useCallback(
    (params) => {
      gridApiRef.current = params.api;

      if (isLoading) {
        params.api.showLoadingOverlay();
      } else if (!rowCount || error) {
        params.api.showNoRowsOverlay();
      }
    },
    [error, isLoading, rowCount]
  );

  useEffect(() => {
    const api = gridApiRef.current;

    if (!api) {
      return;
    }

    if (isLoading) {
      api.showLoadingOverlay();
      return;
    }

    if (error || rowCount === 0) {
      api.showNoRowsOverlay();
      return;
    }
    api.hideOverlay();
  }, [error, isLoading, rowCount]);

  const toggleShowUpcoming = (value) => {
    setShowUpcoming(value);
    // Apply sort by check-in date ascending when toggling to show upcoming
    if (value) {
      const api = gridApiRef.current;
      if (api) {
        api.setSortModel([{ colId: "check_in", sort: "asc" }]);
      }
    } else {
      const api = gridApiRef.current;
      if (api) {
        api.setSortModel(null);
      }
    }
  };

  return (
    <>
      <Grid container spacing={3}>
        <>
          <Grid container item xs={6} justifyContent="space-between">
            <ToggleButtonGroup
              value={orderType}
              exclusive
              size="small"
              onChange={handleOrderType}
              aria-label="order type"
            >
              <ToggleButton value="all" aria-label="left aligned">
                All
              </ToggleButton>
              <ToggleButton value="package" aria-label="left aligned">
                Packages
              </ToggleButton>
              <ToggleButton value="condo" aria-label="left aligned">
                Condos
              </ToggleButton>
            </ToggleButtonGroup>
            <FormControlLabel
              control={
                // eslint-disable-next-line react/jsx-wrap-multilines
                <Checkbox
                  onChange={() => toggleShowUpcoming(!showUpcoming)}
                  value="showUpcoming"
                  checked={showUpcoming}
                />
              }
              label="Show Upcoming Check-Ins Only"
            />
          </Grid>

          {/* Show button only if user has write permission  */}
          {!!write && (
            <Grid
              container
              item
              xs={6}
              spacing={2}
              alignItems="center"
              justifyContent="flex-end"
            >
              {/* Export Button */}
              <Grid item>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={onBtnExport}
                  disabled={isLoading || rowCount === 0}
                >
                  Export CSV
                </Button>
              </Grid>
            </Grid>
          )}

          <Grid item xs={12}>
            <div
              className={
                mode === "dark" ? "ag-theme-alpine-dark" : "ag-theme-alpine"
              }
              style={{ height: 1000, width: "100%" }}
            >
              <AgGridReact
                rowData={rowData}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                enableCellTextSelection
                context={{ history }}
                animateRows
                overlayLoadingTemplate="<span class='ag-overlay-loading-center'>Loading orders...</span>"
                overlayNoRowsTemplate={
                  errorMessage
                    ? `<span class='ag-overlay-no-rows-center'>${escapeOverlayMessage(
                        errorMessage
                      )}</span>`
                    : "<span class='ag-overlay-no-rows-center'>No orders to display</span>"
                }
                onGridReady={onGridReady}
                onCellValueChanged={onCellValueChanged}
              />
            </div>
          </Grid>
          <Snackbar
            open={showSuccess}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
          >
            <MuiAlert
              elevation={6}
              variant="filled"
              onClose={handleSnackbarClose}
              severity="success"
              action={
                snackbarLink ? (
                  <Button
                    size="small"
                    onClick={() => history.push(snackbarLink)}
                  >
                    View
                  </Button>
                ) : null
              }
            >
              {snackbarContent}
            </MuiAlert>
          </Snackbar>
        </>
      </Grid>
    </>
  );
};

export default OrdersPage;
